import {newSchemaService} from '@wix/document-services-json-schemas'
import {validationService} from '@wix/dm-validation'
import type {Experiment, Pointer} from '@wix/document-services-types'
import {EventEmitter} from 'events'
import _ from 'lodash'
import {
    type CoreLogger,
    createFragmentStore,
    createStoreFromJS,
    type DocumentDataTypes,
    FragmentStore,
    type FragmentStoreMap,
    PAGE_FRAGMENT_TYPE,
    type PostSetOperation,
    type PostTransactionOperation,
    type SnapshotDal
} from '../../src'
import {createDal, type CustomGetter, type DAL} from '../../src/dal/documentManagerDal'
import {createStore, DalValue, DmStore} from '../../src/dal/store'
import {getInnerPointer, getPointer, getSignaturePointer} from '../../src/utils/pointerUtils'
import {createTestExperimentInstance} from './experiments'
import {createTestLogger} from './testLogger'
import {createFragmentStoreFromJS} from '../../src/dal/fragment/fragmentStore'

const defaultType = 't'
const defaultFragmentType = 'data'
const defaultViewerType = 'renderFlags'
const basedOnSignatureType = 'basedOnSignature'

const transformValuesToJs = (dal: DAL, snapshot: SnapshotDal, values: any, fragmentType?: string) => {
    return _([])
        .concat(values)
        .reduce((js, value: any) => {
            const dataType = value.dmType || defaultType
            const pointer = getPointer(value.id, dataType, {fragmentType})

            if (!_.has(js, dataType)) {
                js[dataType] = {}
            }

            const {value: signValue, basedOnSignature} = dal.sign(pointer, value, snapshot)

            if (signValue && dal.hasSignature(pointer)) {
                if (!_.has(js, basedOnSignatureType)) {
                    js[basedOnSignatureType] = {}
                }
                js[basedOnSignatureType][dal.getBasedOnSignatureId(pointer.type, pointer.id)] = value.isDeleted
                    ? undefined
                    : basedOnSignature
            }

            js[dataType][pointer.id] = value.isDeleted ? undefined : signValue
            return js
        }, {})
}
const createChangesForRebase = (dal: DAL, snapshot: SnapshotDal, values: any) =>
    createStoreFromJS(transformValuesToJs(dal, snapshot, values))

const createFragmentsChangesForRebase = (dal: DAL, snapshot: SnapshotDal, values: any, fragmentType?: string) => {
    const transformedData = transformValuesToJs(dal, snapshot, values, fragmentType)
    const js = fragmentType ? {[fragmentType]: transformedData} : {[PAGE_FRAGMENT_TYPE]: transformedData}
    return createFragmentStoreFromJS(js)
}

function convertTestValueToDalValue(namespace: string, id: string, value: DalValue) {
    // Most dal-related extension tests assume an object with a data property
    // This makes the tests ergonomic but less flexible
    // To allow all other cases as well, one can mark the test data as `dataField: false`
    if (value.dataField === false) {
        const clean = _.omit(value, ['data', 'dmType', 'dataField', 'fragmentType'])
        return {
            ...(_.isString(value.data) ? {data: value.data} : value.data),
            ...clean,
            id
        }
    }
    return {
        ...value,
        type: value.type ?? 'testData'
    }
}

const customSet = (setter: (pointer: Pointer, value: DalValue) => void, ...args: any[]) => {
    args.forEach(value => {
        const namespace = value.dmType ?? defaultType
        const pointer = getPointer(value.id, namespace, {fragmentType: value.fragmentType})
        const valueForDal = convertTestValueToDalValue(namespace, pointer.id, value)
        setter(pointer, value.isDeleted ? undefined : valueForDal)
    })
}

const setItems = (dal: DAL, ...args: any[]) =>
    customSet((pointer, value) => (value !== undefined ? dal.set(pointer, value) : dal.remove(pointer)), ...args)

const setAndCommit = (dal: DAL, ...args: any[]): SnapshotDal => {
    setItems(dal, ...args)
    return dal.commitTransaction('testDal')
}

export const createStoreWithChanges = (...args: any[]): DmStore => {
    const changes = createStore()
    customSet((pointer, value) => changes.set(pointer, value), ...args)
    return changes
}

const createFragmentStoreWithChanges = (...args: any[]): FragmentStore => {
    const changes = createFragmentStore()
    customSet((pointer, value) => changes.set(pointer, value), ...args)
    return changes
}

const mergeToApprovedStore = (dal: DAL, ...args: any[]): SnapshotDal => {
    const changes = createStoreWithChanges(...args)
    return dal.mergeToApprovedStore(changes)
}

const mergeToApprovedFragmentStore = (dal: DAL, ...args: any[]): SnapshotDal => {
    const changes = createFragmentStoreWithChanges(...args)
    return dal.mergeToApprovedStore(changes)
}

const initializeDal = (
    dal: DAL,
    {
        approvedItems,
        tentativeItems,
        openTransactionItems
    }: {approvedItems?: any; tentativeItems?: any; openTransactionItems?: any}
) => {
    const approvedSnapshots = _.map(approvedItems, values => mergeToApprovedFragmentStore(dal, ...[].concat(values)))
    const tentativeSnapshots = _.map(tentativeItems, values => setAndCommit(dal, ...[].concat(values)))
    setItems(dal, ..._([]).concat(openTransactionItems).compact().value())
    return [...approvedSnapshots, ...tentativeSnapshots]
}

const initializeFragmentDal = (
    dal: DAL,
    {
        approvedItems,
        tentativeItems,
        openTransactionItems
    }: {approvedItems?: any; tentativeItems?: any; openTransactionItems?: any}
) => {
    const approvedSnapshots = _.map(approvedItems, values => mergeToApprovedFragmentStore(dal, ...[].concat(values)))
    const tentativeSnapshots = _.map(tentativeItems, values => setAndCommit(dal, ...[].concat(values)))
    setItems(dal, ..._([]).concat(openTransactionItems).compact().value())
    return [...approvedSnapshots, ...tentativeSnapshots]
}

const defaultPointer = (id: string, innerPath?: string[]) =>
    innerPath ? getInnerPointer(getPointer(id, defaultType), innerPath) : getPointer(id, defaultType)
const defaultFragmentPointer = (id: string, fragmentType: string = 'AppState', innerPath?: string[]) =>
    innerPath
        ? getInnerPointer(getPointer(id, defaultFragmentType, {fragmentType}), innerPath)
        : getPointer(id, defaultFragmentType, {fragmentType})
const getTypeFromTentativeStore = (dal: DAL, type?: string) => dal._tentativeStore.namespaceAsJson(type ?? defaultType)
const getTypeFromApprovedStore = (dal: DAL, type?: string) => dal._store.namespaceAsJson(type ?? defaultType)
const getTypeFromCurrentOpenTransaction = (dal: DAL, type?: string) =>
    dal._currentTransaction.store.namespaceAsJson(type ?? defaultType)
const getTentativeStore = (dal: DAL) => dal._tentativeStore.asJson()
const getApprovedStore = (dal: DAL) => dal._store.asJson()
const getCurrentOpenTransaction = (dal: DAL) => dal._currentTransaction.store.asJson()
const getSignatureForApprovedItem = (dal: DAL, id: string) =>
    dal.getLastApprovedSnapshot().getValue(getSignaturePointer(getPointer(id, defaultType)))
const getTypeFromFragmentsCurrentOpenTransaction = (dal: DAL, type?: string) =>
    dal._getMergedOpenTransactionStoreAsJson?.()[type ?? defaultFragmentType]
const getTypeFromFragmentsTentativeStore = (dal: DAL, type?: string) =>
    dal._getMergedTentativeStoreAsJson?.()[type ?? defaultFragmentType]
const getTypeFromFragmentsApprovedStore = (dal: DAL, type?: string) =>
    dal._getMergedApprovedStoreAsJson?.()[type ?? defaultFragmentType]

const dalUtils = {
    createChangesForRebase,
    createFragmentsChangesForRebase,
    setItems,
    setAndCommit,
    mergeToApprovedStore,
    mergeToApprovedFragmentStore,
    initializeDal,
    initializeFragmentDal,
    defaultPointer,
    getTypeFromTentativeStore,
    getTypeFromApprovedStore,
    getTypeFromCurrentOpenTransaction,
    getTentativeStore,
    getApprovedStore,
    getCurrentOpenTransaction,
    getSignatureForApprovedItem,
    getTypeFromFragmentsCurrentOpenTransaction,
    getTypeFromFragmentsTentativeStore,
    getTypeFromFragmentsApprovedStore,
    defaultFragmentPointer,
    defaultViewerType,
    defaultType
}

interface TestDalArgs {
    experimentInstance?: Experiment
    logger?: CoreLogger
    postTransactionOperations?: Record<string, PostTransactionOperation>
    dmTypes?: DocumentDataTypes
    customGetters?: Record<string, CustomGetter>
    experimentOverrides?: Record<string, string>
    eventEmitter?: EventEmitter
    configOverrides?: Record<string, any>
    postSetOperations?: PostSetOperation[]
    initialStore?: FragmentStoreMap
}

const createTestDal = (
    {
        dmTypes,
        customGetters,
        experimentInstance,
        logger,
        postSetOperations,
        experimentOverrides,
        eventEmitter,
        configOverrides,
        initialStore
    }: TestDalArgs = {},
    schemasToRegister: any = {}
) => {
    const experiment = experimentInstance ?? createTestExperimentInstance(experimentOverrides)
    const validationServiceInstance = validationService.staticInstance
    _.forEach(schemasToRegister, (schemas, fragmentType) => {
        validationServiceInstance.registerFragmentSchemas(fragmentType, schemas)
    })
    const _logger = logger ?? createTestLogger()

    return createDal({
        coreConfig: {
            experimentInstance: experiment,
            logger: _logger,
            schemaService:
                configOverrides?.schemaService ??
                newSchemaService.getInstanceByExperiments(experiment, {
                    enforceStrictValidationEverywhere: configOverrides?.enforceStrictValidationEverywhere,
                    enforceNoWhitelistDalStrictSchemaValidation:
                        configOverrides?.enforceNoWhitelistDalStrictSchemaValidation
                }),
            validationService: validationServiceInstance,
            signatureSeed: 'sig',
            supportedFragments: {
                FragmentIndex: {
                    loadOnInit: true
                },
                AppState: {
                    loadOnInit: true
                }
            },
            transactionActionsLimit: 500,
            minNumOfPagesToCalculateRenderHints: 0,
            ...configOverrides
        },
        postSetOperations: postSetOperations ?? [],
        dmTypes: _.assign({[defaultType]: {hasSignature: true}}, dmTypes),
        customGetters: _.assign({}, customGetters),
        eventEmitter: eventEmitter ?? new EventEmitter(),
        initialStore
    })
}

export {createTestDal, defaultType, defaultFragmentType, dalUtils}
